import { Component, computed, input } from '@angular/core'
import { ColorName } from '../../design-system'
import { Icon, IconComponent } from '../../icon'
import { Illustration, IllustrationComponent } from '../../illustration'

/**
 * Displays placeholder content when the actual content has either errored or is empty. Optional
 * `ng-content` is projected underneath the message.
 *
 * @example
 * ```html
 * <ftr-placeholder-state
 *   title="No Recording Requests"
 *   message="When customers place requests for recordings, they will appear here."
 *   icon="RecordingRequest"
 *   illustration="recording-requests"
 * >
 *   <button>Click me!</button>
 * </ftr-placeholder-state>
 * ```
 */
@Component({
  selector: 'ftr-placeholder-state',
  templateUrl: './placeholder-state.component.html',
  styleUrl: './placeholder-state.component.css',
  imports: [IconComponent, IllustrationComponent],
})
export class PlaceholderStateComponent {
  readonly icon = input.required<Icon>()
  readonly title = input<string>()
  readonly message = input<string>()
  readonly size = input<PlaceholderStateSize>(PlaceholderStateSize.FullPage)
  readonly iconColor = input<ColorName>()
  readonly illustration = input<Illustration>()

  protected readonly isFullPage = computed<boolean>(() => this.size() === PlaceholderStateSize.FullPage)

  protected readonly classes = computed(() => ({
    'placeholder-state': true,
    'placeholder-state--full-page': this.size() === PlaceholderStateSize.FullPage,
    'placeholder-state--medium': this.size() === PlaceholderStateSize.Medium,
    'placeholder-state--small': this.size() === PlaceholderStateSize.Small,
    'placeholder-state--tiny': this.size() === PlaceholderStateSize.Tiny,
  }))
}

export const PlaceholderStateSize = {
  FullPage: 'full-page',
  Medium: 'medium',
  Small: 'small',
  Tiny: 'tiny',
}
export type PlaceholderStateSize = (typeof PlaceholderStateSize)[keyof typeof PlaceholderStateSize]
